// Calendar data conversion utilities
import { UnifiedCalendarEvent } from '@/types/unifiedCalendar';
import { safeJsonParse } from '@/lib/utils';

/**
 * Convert database event (from calendar_events_enhanced) to unified format
 */
export function convertDbEventToUnified(dbEvent: any): UnifiedCalendarEvent {
  // Parse attendees
  let attendeesList: Array<{ email: string; name?: string; status?: any; optional?: boolean }> = [];
  try {
    if (dbEvent.attendees) {
      const parsed = typeof dbEvent.attendees === 'string' ? safeJsonParse(dbEvent.attendees) : dbEvent.attendees;
      attendeesList = Array.isArray(parsed) ? parsed.map((a: any) => ({
        email: a.email || a,
        name: a.name,
        status: a.status,
        optional: a.optional
      })) : [];
    }
  } catch {
    attendeesList = [];
  }

  // Parse attachments
  let attachmentsList: Array<{ title: string; url: string; mimeType?: string }> = [];
  try {
    if (dbEvent.attachments) {
      const parsed = typeof dbEvent.attachments === 'string' ? safeJsonParse(dbEvent.attachments) : dbEvent.attachments;
      attachmentsList = Array.isArray(parsed) ? parsed : [];
    }
  } catch {
    attachmentsList = [];
  }

  return {
    id: dbEvent.id,
    calendarId: dbEvent.calendarId || dbEvent.calendar_id || 'default',
    provider: (dbEvent.source || 'manual') as any,
    
    title: dbEvent.title || '(No Title)',
    description: dbEvent.description,
    location: dbEvent.location,
    
    start: dbEvent.startTime || dbEvent.start_time || dbEvent.startDate || dbEvent.start_date,
    end: dbEvent.endTime || dbEvent.end_time || dbEvent.endDate || dbEvent.end_date,
    timezone: dbEvent.timezone,
    allDay: Number(dbEvent.isAllDay || dbEvent.is_all_day) > 0,
    
    recurring: Number(dbEvent.isRecurring || dbEvent.is_recurring) > 0,
    recurrenceRule: dbEvent.recurrenceRule || dbEvent.recurrence_rule,
    
    organizer: dbEvent.organizerEmail ? {
      email: dbEvent.organizerEmail || dbEvent.organizer_email,
      name: dbEvent.organizerName || dbEvent.organizer_name
    } : undefined,
    
    attendees: attendeesList.length > 0 ? attendeesList : undefined,
    
    status: (dbEvent.status || 'confirmed') as any,
    visibility: (dbEvent.visibility || 'public') as any,
    
    created: dbEvent.createdAt || dbEvent.created_at,
    updated: dbEvent.updatedAt || dbEvent.updated_at,
    color: dbEvent.color,
    
    htmlLink: dbEvent.htmlLink,
    attachments: attachmentsList.length > 0 ? attachmentsList : undefined,
    
    meetingLink: dbEvent.meetingLink || extractMeetingLink(dbEvent.conferenceData),
    
    aiSummary: dbEvent.aiSummary || dbEvent.ai_summary,
    aiPriority: dbEvent.aiPriority || dbEvent.ai_priority,
    aiAgenda: dbEvent.aiAgenda || dbEvent.ai_agenda,
    
    raw: dbEvent.providerData || dbEvent.provider_data
  };
}

/**
 * Extract meeting link from conference data
 */
function extractMeetingLink(conferenceData: any): string | undefined {
  if (!conferenceData) return undefined;
  
  try {
    const parsed = typeof conferenceData === 'string' ? safeJsonParse(conferenceData) : conferenceData;
    return parsed?.entryPoints?.[0]?.uri || parsed?.joinUrl;
  } catch {
    return undefined;
  }
}

/**
 * Calculate event duration in minutes
 */
export function calculateEventDuration(event: UnifiedCalendarEvent): number {
  const start = new Date(event.start).getTime();
  const end = new Date(event.end).getTime();
  return Math.round((end - start) / (1000 * 60));
}

/**
 * Check if event is happening now
 */
export function isEventHappeningNow(event: UnifiedCalendarEvent): boolean {
  const now = new Date().getTime();
  const start = new Date(event.start).getTime();
  const end = new Date(event.end).getTime();
  return now >= start && now <= end;
}

/**
 * Check if event is upcoming (within next 24 hours by default)
 */
export function isEventUpcoming(event: UnifiedCalendarEvent, hoursAhead: number = 24): boolean {
  const now = new Date().getTime();
  const start = new Date(event.start).getTime();
  const threshold = now + hoursAhead * 60 * 60 * 1000;
  return start > now && start <= threshold;
}

/**
 * Check if event is in the past
 */
export function isEventPast(event: UnifiedCalendarEvent): boolean {
  const now = new Date().getTime();
  const end = new Date(event.end).getTime();
  return end < now;
}

/**
 * Group events by date (YYYY-MM-DD format)
 */
export function groupEventsByDate(
  events: UnifiedCalendarEvent[]
): Map<string, UnifiedCalendarEvent[]> {
  const grouped = new Map<string, UnifiedCalendarEvent[]>();
  
  for (const event of events) {
    const date = new Date(event.start).toISOString().split('T')[0];
    
    if (!grouped.has(date)) {
      grouped.set(date, []);
    }
    
    grouped.get(date)!.push(event);
  }
  
  return grouped;
}
